import { useCallback, useState } from 'react';
import {
  HISTORY_AI_LABEL,
  HISTORY_COLUMNS,
  HISTORY_EMPTY,
  HISTORY_EYEBROW,
  HISTORY_FILTER_CLEAR_ALL,
  HISTORY_FILTER_CLEAR_LABEL,
  HISTORY_NOTES_LABEL,
  HISTORY_TITLE,
} from '../constants';
import FilterPopover from './FilterPopover';
import {
  AiMark,
  AttemptTag,
  BodyCell,
  CaseTag,
  ChipClear,
  ClearFiltersButton,
  EmptyNote,
  EventName,
  FilterBar,
  FilterButton,
  FilterChip,
  FilterChips,
  Funnel,
  HeaderCell,
  HistoryEyebrow,
  HistoryHeading,
  HistoryRoot,
  HistoryTable,
  HistoryTitle,
  NotesButton,
  TableWrap,
} from './styled';
import type { HistoryFilterColumn, HistoryFilters, HistorySectionProps } from './types';
import {
  cloneHistoryFilters,
  emptyHistoryFilters,
  getActiveFilterColumns,
  getAttemptRefView,
  getDistinctFilterValues,
  getFilterChipSummary,
  getHistoryEventLabel,
  getHistoryTimeLabel,
  getPartnerWorkerNotesUrl,
  isHistoryRowVisible,
  sortHistoryNewestFirst,
} from './utils';

const HistorySection = ({ history, workerId }: HistorySectionProps) => {
  const [filters, setFilters] = useState<HistoryFilters>(emptyHistoryFilters);
  const [openColumn, setOpenColumn] = useState<HistoryFilterColumn | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const rows = sortHistoryNewestFirst(history);
  const visibleRows = rows.filter((item) => isHistoryRowVisible(item, filters));
  const activeColumns = getActiveFilterColumns(filters);
  const closeFilter = useCallback(() => {
    setOpenColumn(null);
    setAnchorEl(null);
  }, []);

  const updateColumn = (column: HistoryFilterColumn, update: (values: Set<string>) => void) => {
    setFilters((current) => {
      const next = cloneHistoryFilters(current);
      update(next[column]);
      return next;
    });
  };

  const openFilter = (column: HistoryFilterColumn, trigger: HTMLElement) => {
    if (openColumn === column) {
      closeFilter();
      return;
    }
    setOpenColumn(column);
    setAnchorEl(trigger);
  };

  return (
    <HistoryRoot aria-label={HISTORY_TITLE} data-testid="idv-history-section">
      <HistoryHeading>
        <div>
          <HistoryEyebrow>{HISTORY_EYEBROW}</HistoryEyebrow>
          <HistoryTitle>{HISTORY_TITLE}</HistoryTitle>
        </div>
        <NotesButton href={getPartnerWorkerNotesUrl(workerId)} target="_blank" rel="noopener noreferrer">
          {HISTORY_NOTES_LABEL}
        </NotesButton>
      </HistoryHeading>
      {activeColumns.length > 0 ? (
        <FilterBar data-testid="idv-history-filter-bar">
          <FilterChips>
            {activeColumns.map((column) => (
              <FilterChip key={column}>
                {HISTORY_COLUMNS[column]}: <b>{getFilterChipSummary(rows, column, filters[column])}</b>
                <ChipClear
                  type="button"
                  aria-label={HISTORY_FILTER_CLEAR_LABEL(HISTORY_COLUMNS[column])}
                  onClick={() => updateColumn(column, (values) => values.clear())}
                >
                  ×
                </ChipClear>
              </FilterChip>
            ))}
          </FilterChips>
          <ClearFiltersButton type="button" onClick={() => setFilters(emptyHistoryFilters())}>
            {HISTORY_FILTER_CLEAR_ALL}
          </ClearFiltersButton>
        </FilterBar>
      ) : null}
      <TableWrap>
        <HistoryTable>
          <thead>
            <tr>
              <HeaderCell>{HISTORY_COLUMNS.time}</HeaderCell>
              <FilterHeader
                column="event"
                filtered={filters.event.size > 0}
                open={openColumn === 'event'}
                onOpen={openFilter}
              />
              <FilterHeader
                column="actor"
                filtered={filters.actor.size > 0}
                open={openColumn === 'actor'}
                onOpen={openFilter}
              />
              <HeaderCell>{HISTORY_COLUMNS.details}</HeaderCell>
              <FilterHeader
                column="attempt"
                filtered={filters.attempt.size > 0}
                open={openColumn === 'attempt'}
                onOpen={openFilter}
              />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item, index) => {
              const attemptRef = getAttemptRefView(item.attempt_ref);
              const Tag = attemptRef.kind === 'case' ? CaseTag : AttemptTag;
              return (
                <tr key={`${item.created_at}-${item.event}-${item.attempt_ref}-${index}`} data-testid="idv-history-row">
                  <BodyCell>{getHistoryTimeLabel(item.created_at)}</BodyCell>
                  <BodyCell>
                    <EventName>{getHistoryEventLabel(item.event)}</EventName>
                  </BodyCell>
                  <BodyCell>{item.actor}</BodyCell>
                  <BodyCell>{item.details}</BodyCell>
                  <BodyCell>
                    <Tag data-testid="idv-history-attempt" data-kind={attemptRef.kind}>
                      {attemptRef.label}
                      {attemptRef.kind === 'ai' ? <AiMark>{HISTORY_AI_LABEL}</AiMark> : null}
                    </Tag>
                  </BodyCell>
                </tr>
              );
            })}
          </tbody>
        </HistoryTable>
      </TableWrap>
      {visibleRows.length === 0 ? <EmptyNote>{HISTORY_EMPTY}</EmptyNote> : null}
      {openColumn && anchorEl ? (
        <FilterPopover
          column={openColumn}
          anchorEl={anchorEl}
          values={getDistinctFilterValues(rows, openColumn)}
          deselected={filters[openColumn]}
          onToggle={(value, checked) =>
            updateColumn(openColumn, (values) => {
              if (checked) values.delete(value);
              else values.add(value);
            })
          }
          onSetVisible={(values, checked) =>
            updateColumn(openColumn, (selected) => {
              values.forEach((value) => {
                if (checked) selected.delete(value);
                else selected.add(value);
              });
            })
          }
          onClearColumn={() => updateColumn(openColumn, (values) => values.clear())}
          onClose={closeFilter}
        />
      ) : null}
    </HistoryRoot>
  );
};

const FilterHeader = ({
  column,
  filtered,
  open,
  onOpen,
}: {
  column: HistoryFilterColumn;
  filtered: boolean;
  open: boolean;
  onOpen: (column: HistoryFilterColumn, trigger: HTMLElement) => void;
}) => (
  <HeaderCell>
    <FilterButton
      type="button"
      filtered={filtered}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid={`idv-history-filter-${column}`}
      onClick={(event) => onOpen(column, event.currentTarget)}
    >
      {HISTORY_COLUMNS[column]}{' '}
      <Funnel filtered={filtered} aria-hidden="true">
        ▾
      </Funnel>
    </FilterButton>
  </HeaderCell>
);

export default HistorySection;
